<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { onMounted, ref } from "vue";
import { isOrderRepoError } from "@chatfood/dbe-client/errors";
import { useModalHubStore } from "../modal-hub";
import { CHECKOUT_CALLBACK_PARAM } from "~/domain/checkout-callback-param";
import type { IDomainOrderStatus } from "~/domain/order-status";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";
import { useBffErrors } from "~/composables/use-bff-errors";
import { useAnalytics, useBugReporter, useDirectBackend } from "#imports";
import { useBasketStore } from "~/stores/basket";

const css = bemBuilder("checkout-callback");

const { t } = useLocale();
const { route, push } = useNavigation();
const { errorsToString } = useBffErrors();
const { report } = useBugReporter();
const { trackEvent } = useAnalytics();
const { orderRepo } = useDirectBackend();

const modalHub = useModalHubStore();
const basket = useBasketStore();

const statusToFail: IDomainOrderStatus[] = ["unpaid"];
const statusToRetry: IDomainOrderStatus[] = ["created"];

const orderUid = ref();
const orderStatus = ref();
const orderTrackingId = ref();

async function checkOrderStatus(orderId: string) {
  try {
    const res = await orderRepo.getById({ orderId });

    if (!res) {
      throw new Error("Response from server is empty.");
    }

    orderUid.value = res.uid;
    orderStatus.value = res.status;
    orderTrackingId.value = res.trackingId;

    if (statusToRetry.includes(orderStatus.value)) {
      setTimeout(() => checkOrderStatus(orderId), 1000);
      return;
    }

    modalHub.hideLoading();

    if (statusToFail.includes(orderStatus.value)) {
      trackEvent({
        name: "orderFailure",
        payload: {
          orderId,
          orderUid: orderUid.value,
          reason: orderStatus.value,
        },
      });

      modalHub.showError(
        t("components.checkout_callback.payment_failed_title"),
        t("components.checkout_callback.payment_failed_message"),
        t("components.checkout_callback.payment_failed_cta"),
      );
      return;
    }

    trackEvent({
      name: "orderSuccess",
      payload: {
        orderId,
        orderUid: orderUid.value,
      },
    });

    push("order-tracking", { trackingId: orderTrackingId.value });

    basket.removeBasket();
  } catch (error: unknown) {
    let errorMessage = errorsToString(error, "\n");

    if (!errorMessage) {
      errorMessage = t("components.checkout_callback.unknown_error_message");
      report("Unknown error while polling order status", {
        context: {
          error: JSON.stringify(error),
        },
        tags: {
          "order.id": orderId,
        },
      });
    }

    modalHub.hideLoading();

    if (isOrderRepoError(error) && error?.code === "notFound") {
      errorMessage = t("components.checkout_callback.order_not_found");
    }

    modalHub.showError(
      t("components.checkout_callback.unknown_error_title"),
      errorMessage,
      t("components.checkout_callback.unknown_error_cta"),
    );
  }
}

onMounted(() => {
  const orderId = route.value.query[CHECKOUT_CALLBACK_PARAM] as string;
  if (!orderId) return;
  modalHub.showLoading(
    t("components.checkout_callback.confirming_order_title"),
    t("components.checkout_callback.confirming_order_message"),
  );
  checkOrderStatus(orderId);
});
</script>

<template>
  <div :class="css('')"></div>
</template>

<style lang="scss" scoped>
.checkout-callback {
  width: 0;
  height: 0;
}
</style>
